import { useSyncQueueStore } from '@/lib/helpers/bgSync/useSyncQueueStore.ts'
import type { QueueItemT } from '@/lib/types/bgSyncTypes.ts'

// Set of `meta.type` values whose queued items, while present, mean the
// optimistic React Query cache we wrote for that resource must not be
// refetched. While these entries are still in flight the server only sees a
// partial state — refetching now would tear down the optimistic UI and
// replace it with whatever fraction of the bulk has reached the database so
// far. Once the queue empties for a type, `useQueueDrainInvalidator` fires a
// single invalidation that reconciles cache with server-truth.
const ACTIVE_METHODS = new Set(['POST', 'PATCH'])

// A licence hold belongs to the whole requestUUID group. A row queued into the group after the
// refusal carries no flag until the next sync pass propagates it, so the check is on the group.
function licenceHeldGroups(queue: QueueItemT[]): Set<string> {
  const held = new Set<string>()
  for (const item of queue) {
    if (item.metadata.licenceBlocked) held.add(item.metadata.requestUUID)
  }
  return held
}

function isActiveEntry(item: QueueItemT, type: string, held: Set<string>): boolean {
  return (
    item.metadata.type === type &&
    ACTIVE_METHODS.has(item.metadata.method) &&
    !item.metadata.permanentFailure &&
    // A licence-held write may wait days; treating it as in flight would suppress every
    // issue-list refresh (Mercure and post-mutation) across all projects for that long.
    !held.has(item.metadata.requestUUID)
  )
}

export function hasPendingQueueEntriesForType(type: string): boolean {
  const queue = useSyncQueueStore.getState().data
  const held = licenceHeldGroups(queue)
  return queue.some(item => isActiveEntry(item, type, held))
}

export function countPendingQueueEntriesForType(type: string, queue: QueueItemT[]): number {
  const held = licenceHeldGroups(queue)
  let n = 0
  for (const item of queue) if (isActiveEntry(item, type, held)) n++
  return n
}
